import { Button } from '@/components/ui/button'
import { revokeCurrentServerSession } from '@/utils/session-actions'
import { useAuth } from '@clerk/clerk-react'
import { AlertCircle, Clock, LogIn, Mail, Phone, Shield } from 'lucide-react'

const UnderVerification = () => {
  const { signOut } = useAuth()

  return (
    <div className='min-h-screen bg-background flex flex-col'>
      {/* Header */}
      <div className='flex-1 flex items-center justify-center p-4'>
        <div className='w-full max-w-2xl'>
          {/* Main Card */}
          <div className='bg-card rounded-2xl shadow-xl border border-border overflow-hidden'>
            {/* Header Section */}
            <div className='bg-card p-8 text-center border-b border-border'>
              <div className='inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4'>
                <Shield className='w-8 h-8 text-primary' />
              </div>
              <h1 className='text-3xl font-bold text-foreground mb-2'>Account Verification Required</h1>
              <p className='text-muted-foreground text-lg'>Your account is currently under review</p>
            </div>

            {/* Content Section */}
            <div className='p-8'>
              {/* Status Indicator */}
              <div className='flex items-center justify-center mb-6'>
                <div className='flex items-center space-x-2 bg-muted border border-border rounded-full px-4 py-2'>
                  <AlertCircle className='w-5 h-5 text-orange-400 dark:text-orange-200' />
                  <span className='text-orange-400 dark:text-orange-200 font-medium'>Pending Verification</span>
                </div>
              </div>

              {/* Main Message */}
              <div className='text-center mb-8'>
                <p className='text-muted-foreground text-lg leading-relaxed mb-4'>
                  Before you can access this platform, we need to verify your account. This process typically takes up
                  to 24 hours.
                </p>
                <p className='text-muted-foreground/80'>
                  You will receive an email notification once your account has been verified.
                </p>
              </div>

              {/* Timeline */}
              <div className='bg-muted rounded-xl p-6 mb-8'>
                <h3 className='text-lg font-semibold text-foreground mb-4 flex items-center'>
                  <Clock className='w-5 h-5 mr-2 text-primary' />
                  What happens next?
                </h3>
                <div className='space-y-3'>
                  <div className='flex items-start space-x-3'>
                    <div className='w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5'>
                      <span className='text-primary-foreground text-xs font-bold'>1</span>
                    </div>
                    <div>
                      <p className='text-foreground font-medium'>Account Review</p>
                      <p className='text-muted-foreground text-sm'>Our team is reviewing your registration details</p>
                    </div>
                  </div>
                  <div className='flex items-start space-x-3'>
                    <div className='w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5'>
                      <span className='text-primary-foreground text-xs font-bold'>2</span>
                    </div>
                    <div>
                      <p className='text-foreground font-medium'>Email Notification</p>
                      <p className='text-muted-foreground text-sm'>
                        You'll receive an email once verification is complete
                      </p>
                    </div>
                  </div>
                  <div className='flex items-start space-x-3'>
                    <div className='w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5'>
                      <span className='text-primary-foreground text-xs font-bold'>3</span>
                    </div>
                    <div>
                      <p className='text-foreground font-medium'>Access Granted</p>
                      <p className='text-muted-foreground text-sm'>You can then log in and start using the platform</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className='text-center mb-8'>
                <div className='flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-muted-foreground'>
                  <a href='mailto:info@cyberensic.com.au' className='hover:text-primary transition-colors'>
                    <div className='flex items-center space-x-2'>
                      <Mail className='w-4 h-4' />
                      <span>info@cyberensic.com.au</span>
                    </div>
                  </a>

                  <a href='tel:+61424582861' className='hover:text-primary transition-colors'>
                    <div className='flex items-center space-x-2'>
                      <Phone className='w-4 h-4' />
                      <span>+61424582861</span>
                    </div>
                  </a>
                </div>
              </div>

              {/* Action Buttons */}
              <div className='flex justify-center'>
                <Button
                  onClick={() => {
                    void (async () => {
                      try {
                        await revokeCurrentServerSession()
                      } catch {
                        // Continue clearing client session even if server revocation fails.
                      }
                      await signOut({ redirectUrl: '/sign-in' })
                    })()
                  }}
                >
                  <LogIn />
                  Back to Login
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className='text-center py-6 text-muted-foreground'>
        <p className='text-sm'>&copy; {new Date().getFullYear()} CISO Adapt. All rights reserved.</p>
      </footer>
    </div>
  )
}

export default UnderVerification
